import React, { useState } from "react";
import { Eye, Image as ImageIcon } from "lucide-react";
import ColorField from "../ColorField";

const DIPLOMA_ELEMENTS = [
  ["logo", "Logótipo (imagem)"],
  ["title", "Título"],
  ["subtitle", "Subtítulo"],
  ["awarded", "Etiqueta \u201cAtribuído a\u201d"],
  ["name", "Nome do aluno"],
  ["school", "Escola"],
  ["message", "Texto do diploma"],
  ["quiz", "Nome do concurso"],
  ["time", "Tempo final"],
  ["avgtime", "Tempo médio da prova"],
  ["ranking", "Posição no ranking"],
  ["footer", "Rodapé"],
];

const DEFAULT_LAYOUT = {
  logo:     { x: 0.5, y: 0.90,  size: 80, font: "regular", color: "", align: "center", visible: false },
  title:    { x: 0.5, y: 0.80,  size: 38, font: "bold",    color: "#FF6B6B", align: "center", visible: true },
  subtitle: { x: 0.5, y: 0.745, size: 17, font: "regular", color: "", align: "center", visible: true },
  awarded:  { x: 0.5, y: 0.655, size: 13, font: "italic",  color: "", align: "center", visible: true },
  name:     { x: 0.5, y: 0.583, size: 38, font: "bold",    color: "", align: "center", visible: true },
  school:   { x: 0.5, y: 0.516, size: 14, font: "regular", color: "", align: "center", visible: true },
  message:  { x: 0.5, y: 0.46,  size: 12, font: "italic",  color: "", align: "center", visible: true },
  quiz:     { x: 0.5, y: 0.40,  size: 14, font: "bold",    color: "", align: "center", visible: true },
  time:     { x: 0.5, y: 0.34,  size: 18, font: "bold",    color: "", align: "center", visible: true },
  avgtime:  { x: 0.5, y: 0.295, size: 12, font: "regular", color: "", align: "center", visible: true },
  ranking:  { x: 0.5, y: 0.24,  size: 12, font: "bold",    color: "", align: "center", visible: true },
  footer:   { x: 0.5, y: 0.108, size: 13, font: "italic",  color: "", align: "center", visible: true },
  qr:       { x: 0.86, y: 0.20, size: 92, visible: true },
};

export default function AdminDiplomaTextSection({ form, setForm, dirty, upload, backendUrl }) {
  const [dLang, setDLang] = useState("pt");
  if (!form) return null;
  const set = (field, val) => { dirty.current.add(field); setForm((f) => ({ ...f, [field]: val })); };
  const LT = ({ base, label, ml, rows = 3, testid }) => {
    const k = `${base}_${dLang}`;
    return (
      <label className="block text-sm">{label} ({dLang.toUpperCase()})
        {ml
          ? <textarea value={form[k] || ""} onChange={(e) => set(k, e.target.value)} rows={rows} className="w-full mt-1 px-3 py-2 rounded-lg border border-[#0B1D3A]/15" data-testid={testid} />
          : <input value={form[k] || ""} onChange={(e) => set(k, e.target.value)} className="w-full mt-1 px-3 py-2 rounded-lg border border-[#0B1D3A]/15" data-testid={testid} />}
      </label>
    );
  };
  const getL = (key, prop) => {
    const lay = form.diploma_layout || {};
    const v = (lay[key] || {})[prop];
    return v === undefined || v === null ? DEFAULT_LAYOUT[key][prop] : v;
  };
  const setL = (key, prop, val) => {
    dirty.current.add("diploma_layout");
    setForm((f) => {
      const lay = { ...(f.diploma_layout || {}) };
      lay[key] = { ...DEFAULT_LAYOUT[key], ...(lay[key] || {}), [prop]: val };
      return { ...f, diploma_layout: lay };
    });
  };
  const BgUploader = ({ label, field }) => (
    <div className="rounded-2xl bg-white p-5 border border-[#0B1D3A]/5">
      <p className="font-outfit font-semibold text-[#0B1D3A] mb-3 flex items-center gap-2"><ImageIcon size={18} /> {label}</p>
      <div className="flex items-center gap-3">
        <input type="file" accept="image/*" data-testid={`bg-uploader-${field}`}
          onChange={(e) => e.target.files?.[0] && upload(e.target.files[0], field)} className="text-sm" />
        {form[field] && (
          <>
            <img src={`${backendUrl}/api/files/${form[field]}`} alt="" className="h-16 w-24 object-cover rounded-lg border" />
            <button onClick={() => set(field, "")} className="text-xs px-2 py-1 rounded-md bg-red-50 text-red-600">Remover</button>
          </>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      <h2 className="font-outfit font-bold text-[#0B1D3A]/70 mt-4">Diplomas — textos, posições e QR</h2>
      <BgUploader label="Template do diploma (imagem A4 paisagem)" field="diploma_template_path" />
      <div className="rounded-2xl bg-white p-5 border border-[#0B1D3A]/5 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <p className="text-sm font-semibold text-[#0B1D3A]">Posicionamento e estilo de cada texto</p>
          <label className="flex items-center gap-2 text-sm">Cor base do texto<ColorField value={form.diploma_text_color || "#0B1D3A"} onChange={(e) => set("diploma_text_color", e.target.value)} className="h-9 w-14 rounded-lg border border-[#0B1D3A]/15"/></label>
        </div>
        <p className="text-xs text-[#0B1D3A]/55">X/Y em fração da página (0–1). X: 0 = esquerda, 0.5 = centro, 1 = direita. Y: 0 = base, 1 = topo.</p>
        <div className="overflow-x-auto">
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr className="text-left text-[#0B1D3A]/60 border-b border-[#0B1D3A]/10">
                <th className="py-2 pr-2">Elemento</th><th className="py-2 px-1">Visível</th><th className="py-2 px-1">X</th><th className="py-2 px-1">Y</th><th className="py-2 px-1">Tam.</th><th className="py-2 px-1">Fonte</th><th className="py-2 px-1">Alinh.</th><th className="py-2 px-1">Cor</th>
              </tr>
            </thead>
            <tbody>
              {DIPLOMA_ELEMENTS.map(([key, label]) => (
                <tr key={key} className="border-b border-[#0B1D3A]/5" data-testid={`diploma-el-${key}`}>
                  <td className="py-1.5 pr-2 font-medium text-[#0B1D3A] whitespace-nowrap">{label}</td>
                  <td className="py-1.5 px-1 text-center"><input type="checkbox" checked={!!getL(key, "visible")} onChange={(e) => setL(key, "visible", e.target.checked)} data-testid={`diploma-${key}-visible`}/></td>
                  <td className="py-1.5 px-1"><input type="number" step="0.01" min="0" max="1" value={getL(key, "x")} onChange={(e) => setL(key, "x", parseFloat(e.target.value))} className="w-16 px-2 py-1 rounded border border-[#0B1D3A]/15" data-testid={`diploma-${key}-x`}/></td>
                  <td className="py-1.5 px-1"><input type="number" step="0.01" min="0" max="1" value={getL(key, "y")} onChange={(e) => setL(key, "y", parseFloat(e.target.value))} className="w-16 px-2 py-1 rounded border border-[#0B1D3A]/15" data-testid={`diploma-${key}-y`}/></td>
                  <td className="py-1.5 px-1"><input type="number" min="6" max="120" value={getL(key, "size")} onChange={(e) => setL(key, "size", parseInt(e.target.value) || 12)} className="w-14 px-2 py-1 rounded border border-[#0B1D3A]/15" data-testid={`diploma-${key}-size`}/></td>
                  <td className="py-1.5 px-1">
                    <select value={getL(key, "font")} onChange={(e) => setL(key, "font", e.target.value)} className="px-1.5 py-1 rounded border border-[#0B1D3A]/15 bg-white" data-testid={`diploma-${key}-font`}>
                      <option value="regular">Normal</option><option value="bold">Negrito</option><option value="italic">Itálico</option>
                    </select>
                  </td>
                  <td className="py-1.5 px-1">
                    <select value={getL(key, "align")} onChange={(e) => setL(key, "align", e.target.value)} className="px-1.5 py-1 rounded border border-[#0B1D3A]/15 bg-white" data-testid={`diploma-${key}-align`}>
                      <option value="left">Esq.</option><option value="center">Centro</option><option value="right">Dir.</option>
                    </select>
                  </td>
                  <td className="py-1.5 px-1"><ColorField value={getL(key, "color") || (form.diploma_text_color || "#0B1D3A")} onChange={(e) => setL(key, "color", e.target.value)} className="h-7 w-10 rounded border border-[#0B1D3A]/15" data-testid={`diploma-${key}-color`}/></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="rounded-xl border border-[#0B1D3A]/10 bg-[#FDFBF7] p-3 space-y-3" data-testid="diploma-qr-block">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <p className="text-sm font-semibold text-[#0B1D3A]">QR Code → ranking público</p>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!getL("qr", "visible")} onChange={(e) => setL("qr", "visible", e.target.checked)} data-testid="diploma-qr-visible"/> Mostrar QR Code no diploma</label>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <label className="block text-xs">X<input type="number" step="0.01" min="0" max="1" value={getL("qr", "x")} onChange={(e) => setL("qr", "x", parseFloat(e.target.value))} className="w-full mt-1 px-2 py-1 rounded border border-[#0B1D3A]/15" data-testid="diploma-qr-x"/></label>
            <label className="block text-xs">Y<input type="number" step="0.01" min="0" max="1" value={getL("qr", "y")} onChange={(e) => setL("qr", "y", parseFloat(e.target.value))} className="w-full mt-1 px-2 py-1 rounded border border-[#0B1D3A]/15" data-testid="diploma-qr-y"/></label>
            <label className="block text-xs">Tamanho<input type="number" min="40" max="200" value={getL("qr", "size")} onChange={(e) => setL("qr", "size", parseInt(e.target.value) || 92)} className="w-full mt-1 px-2 py-1 rounded border border-[#0B1D3A]/15" data-testid="diploma-qr-size"/></label>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="block text-xs md:col-span-2">Legenda do QR ({dLang.toUpperCase()})<input value={form[`diploma_qr_caption_${dLang}`] || ""} onChange={(e) => set(`diploma_qr_caption_${dLang}`, e.target.value)} placeholder="Vê o ranking" className="w-full mt-1 px-2 py-1 rounded border border-[#0B1D3A]/15" data-testid="diploma-qr-caption-pt"/></label>
          </div>
        </div>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <p className="text-xs text-[#0B1D3A]/60">Textos do diploma (modelo automático quando não há template carregado):</p>
          <div className="inline-flex rounded-full border border-[#0B1D3A]/15 overflow-hidden text-xs font-bold" data-testid="diploma-text-lang">
            {[["pt", "PT"], ["br", "BR"], ["en", "EN"]].map(([v, l]) => (
              <button key={v} type="button" onClick={() => setDLang(v)} data-testid={`diploma-text-lang-${v}`}
                className={`px-3 py-1 ${dLang === v ? "bg-[#005F73] text-white" : "bg-white text-[#0B1D3A]/60"}`}>{l}</button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <LT base="diploma_title" label="Título" testid="diploma-title-pt" />
          <LT base="diploma_subtitle" label="Subtítulo" testid="diploma-subtitle-pt" />
          <LT base="diploma_footer" label="Rodapé" testid="diploma-footer-pt" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <LT base="diploma_message" label="Texto do diploma" ml rows={3} testid="diploma-message-pt" />
        </div>
        <div className="flex flex-wrap gap-3 pt-1">
          <button type="button" onClick={() => window.open(`${backendUrl}/api/diploma/preview?lang=pt`, "_blank", "noopener")} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#FF6B6B] text-white font-bold text-sm" data-testid="diploma-preview-pt-btn"><Eye size={15}/> Pré-visualizar (PT)</button>
          <button type="button" onClick={() => window.open(`${backendUrl}/api/diploma/preview?lang=br`, "_blank", "noopener")} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#1B5E3A] text-white font-bold text-sm" data-testid="diploma-preview-br-btn"><Eye size={15}/> Pré-visualizar (BR)</button>
          <button type="button" onClick={() => window.open(`${backendUrl}/api/diploma/preview?lang=en`, "_blank", "noopener")} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#005F73] text-white font-bold text-sm" data-testid="diploma-preview-en-btn"><Eye size={15}/> Preview (EN)</button>
        </div>
        <p className="text-[11px] text-[#0B1D3A]/45">Guarda as alterações antes de pré-visualizar.</p>
      </div>

      {/* Ranking público */}
      <h2 className="font-outfit font-bold text-[#0B1D3A]/70 mt-6">Ranking público</h2>
      <div className="rounded-2xl bg-white p-5 border border-[#0B1D3A]/5 space-y-3">
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={(form.ranking_top_n || 0) === 0} onChange={(e) => set("ranking_top_n", e.target.checked ? 0 : 10)} data-testid="ranking-show-all-checkbox"/>
          Mostrar todos os alunos no ranking
        </label>
        <label className={`block text-sm ${(form.ranking_top_n || 0) === 0 ? "opacity-40 pointer-events-none" : ""}`}>Nº de alunos a mostrar (Top N)
          <input type="number" min="1" max="100" value={form.ranking_top_n || 10} onChange={(e) => set("ranking_top_n", parseInt(e.target.value) || 10)} className="w-full mt-1 px-3 py-2 rounded-lg border border-[#0B1D3A]/15" data-testid="ranking-top-input"/>
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={!!form.show_school_in_ranking} onChange={(e) => set("show_school_in_ranking", e.target.checked)} data-testid="show-school-checkbox"/>
          Mostrar escola no ranking público
        </label>
      </div>
    </div>
  );
}
